import { FeedbackDetailsHeader } from '@modules/feedback/components/FeedbackDetails/FeedbackDetailsHeader';
import { FeedbackDetailsRightPanel } from '@modules/feedback/components/FeedbackDetails/FeedbackDetailsRightPanel';
import { getCombinedMatchedMetadataFromLabels } from '@modules/feedback/components/FeedbackDetails/utils/getCombinedMatchedMetadataFromLabels';
import {
  PREFETCH_TRIGGER_OFFSET,
  RECORDS_PER_PAGE,
} from '@modules/feedback/constants';
import { useUpdateSelectedLabels } from '@modules/feedback/hooks';
import { useGetFeedbackList } from '@modules/feedback/hooks/useGetFeedbackList';
import useFeedbackListStore, {
  AudioSnippetTime,
} from '@modules/feedback/store/useFeedbackListStore';
import { Box, Stack, Typography } from '@mui/material';
import { GetRecordsResponseViewFragment } from 'generated/graphql';
import { isEqual } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';
import scrollIntoView from 'scroll-into-view-if-needed';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';
import { SearchLabel } from 'v2/core/entities/feedback/labels';
import { useGetUser } from 'v2/core/services/user';
import { FeedbackDetailsLeftPanel } from './FeedbackDetailsLeftPanel';

const trackFeedbackPagination = createAnalyticsTracker('feedbackPagination');

interface FeedbackDetailsProps {
  feedback: Feedback;
  onDrawerClose?: () => void;
  selectedLabels?: FeedbackLabel[];
  footer?: ReactNode;
  setCurrentFeedbackIndex?: (index: number) => void;
  setCurrentFeedback?: (feedback: Feedback) => void;
  feedbacks?: Feedback[];
  currentFeedbackIndex?: number;
  totalRecords?: number;
  isLoadingMore?: boolean;
  fetchMore?: ReturnType<typeof useGetFeedbackList>['fetchMore'];
  setFeedbackRecords?: (records: GetRecordsResponseViewFragment) => void;
  selectedAudioSnippetTime?: AudioSnippetTime;
}

export const FeedbackDetails = ({
  feedback,
  onDrawerClose,
  selectedLabels: selectedLabelsToHighlight,
  footer,
  setCurrentFeedback,
  setCurrentFeedbackIndex,
  feedbacks = [],
  currentFeedbackIndex,
  totalRecords = 0,
  isLoadingMore,
  fetchMore,
  setFeedbackRecords,
  selectedAudioSnippetTime,
}: FeedbackDetailsProps) => {
  const [selectedLabels, setSelectedLabels] = useState<FeedbackLabel[]>(
    selectedLabelsToHighlight ?? [],
  );

  const setSelectedAudioSnippet = useFeedbackListStore(
    ({ setSelectedSnippetAudioSnippet }) => setSelectedSnippetAudioSnippet,
  );

  const {
    tenant: {
      dashboardSettings: { showUnTranslatedRecords },
    },
  } = useGetUser();

  const [showOriginalLanguage, setShowOriginalLanguage] = useState(
    showUnTranslatedRecords ?? false,
  );
  const prevSelectedLabels = usePrevious(selectedLabels);

  const isAudioPlaying = useFeedbackListStore(
    (state) => state.feedbacks[feedback.id]?.isPlaying ?? false,
  );

  useUpdateSelectedLabels({
    feedbackID: feedback.id,
    selectedLabels,
    setSelectedLabels,
  });

  useEffect(() => {
    if (selectedAudioSnippetTime) {
      setSelectedAudioSnippet(selectedAudioSnippetTime, feedback.id);
    }
  }, [selectedAudioSnippetTime, setSelectedAudioSnippet, feedback.id]);

  useEffect(() => {
    if (currentFeedbackIndex && currentFeedbackIndex % RECORDS_PER_PAGE === 0) {
      trackFeedbackPagination({
        eventName: 'Feedback Records Page Changed',
        page: currentFeedbackIndex / RECORDS_PER_PAGE + 1,
        view: 'Feedback Details',
      });
    }
  }, [currentFeedbackIndex]);

  const fetchMoreRecords = useCallback(async () => {
    try {
      const resp = await fetchMore?.();
      const getRecords = resp?.data?.executeEQL
        ?.result as GetRecordsResponseViewFragment;
      setFeedbackRecords?.(getRecords);
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canfetchMore = feedbacks.length < totalRecords;

  const fetchMoreIndex = feedbacks
    ? feedbacks.length - PREFETCH_TRIGGER_OFFSET
    : undefined;

  useEffect(() => {
    // already fetched all the records
    if (!canfetchMore || !fetchMoreIndex) {
      return;
    }

    if (currentFeedbackIndex === fetchMoreIndex) {
      fetchMoreRecords();
    }
  }, [currentFeedbackIndex, fetchMoreIndex, fetchMoreRecords, canfetchMore]);

  useEffect(() => {
    // If the selected labels have changed, we need to update the highlights
    // to match the new selection. We need to scroll to the position where the
    // newly added highlight is.
    //
    // We should also update the highlights if the user has selected a label
    // and not when the user has deselected a label.
    if (!selectedLabels.length) {
      return;
    }

    if (isAudioPlaying) {
      return; // if audio is playing, then don't scroll to selected label
    }

    const latestSelectedLabelID = selectedLabels?.[0]?.id ?? '';
    const latestSelectedLabelMatchMsgIndex =
      getCombinedMatchedMetadataFromLabels(selectedLabels)
        .filter((metadata) => metadata.id === latestSelectedLabelID)
        .sort((a, b) => (a.msgIDx ?? 0) - (b.msgIDx ?? 0))?.[0]?.msgIDx ?? 0;

    const scrollElId =
      latestSelectedLabelID + latestSelectedLabelMatchMsgIndex.toString();

    if (!scrollElId) {
      return;
    }

    const scrollEl = document.querySelectorAll(
      `#feedback-details [data-highlight-id="${scrollElId}"]`,
    )[0];

    if (!scrollEl) {
      return;
    }

    scrollIntoView(scrollEl, {
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
      boundary: document.getElementById('feedback-details'),
      skipOverflowHiddenElements: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.id, prevSelectedLabels, selectedLabels]);

  const searchLabel = useMemo(
    () =>
      feedback.labels?.filter(
        (label): label is SearchLabel => label.type === 'SEARCH',
      )[0],
    [feedback.labels],
  );

  useEffect(() => {
    if (!selectedLabelsToHighlight?.length) {
      return;
    }
    setSelectedLabels((prev) =>
      isEqual(selectedLabelsToHighlight, prev)
        ? prev
        : selectedLabelsToHighlight ?? [],
    );
  }, [selectedLabelsToHighlight]);

  useEffect(() => {
    return () => {
      setSelectedLabels(selectedLabelsToHighlight ?? []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.id]);

  useEffect(() => {
    // Use entityLabels and reasonLabels to highlight the feedback. But, don't
    // use searchlabel from the prop as searchLabel don't have an unique id.
    if (searchLabel) {
      setSelectedLabels((prev) => [...prev, searchLabel]);
      return;
    }
  }, [searchLabel]);

  useEffect(() => {
    setShowOriginalLanguage(showUnTranslatedRecords ?? false);
  }, [feedback, showUnTranslatedRecords]);

  const shouldShowLoader = (() => {
    if (!isLoadingMore) {
      return false;
    }

    // show loader on the last feedback
    if (
      currentFeedbackIndex !== undefined &&
      currentFeedbackIndex < feedbacks.length - 1
    ) {
      return false;
    }

    return true;
  })();

  return (
    <Stack height="100%" id="feedback-details">
      <FeedbackDetailsHeader
        feedback={feedback}
        onDrawerClose={onDrawerClose}
        sourceV2={feedback.sourceV2}
        setCurrentFeedbackIndex={setCurrentFeedbackIndex}
        setCurrentFeedback={setCurrentFeedback}
        feedbacks={feedbacks}
        currentFeedbackIndex={currentFeedbackIndex}
        totalRecords={totalRecords}
        isLoadingMore={shouldShowLoader}
      />
      {shouldShowLoader ? (
        <Stack justifyContent="center" alignItems="center" height="100%">
          <Typography variant="body1">Loading feedback...</Typography>
        </Stack>
      ) : (
        <Stack direction="row" spacing={2} p={2} height="100%" overflow="auto">
          <Box
            sx={{
              width: 2 / 3,
              overflow: 'hidden',
            }}
          >
            <FeedbackDetailsLeftPanel
              key={currentFeedbackIndex}
              feedback={feedback}
              selectedLabels={selectedLabels}
              showOriginalLanguage={showOriginalLanguage}
              setSelectedLabels={setSelectedLabels}
            />
          </Box>

          <FeedbackDetailsRightPanel
            feedback={feedback}
            setSelectedLabels={setSelectedLabels}
            selectedLabels={selectedLabels}
            showOriginalLanguage={showOriginalLanguage}
            setShowOriginalLanguage={setShowOriginalLanguage}
          />
        </Stack>
      )}
      {footer}
    </Stack>
  );
};
